// Calendar dates belong to the business, independently of the server/device zone.
import markets from '../data/markets.json';
const cache = new Map();
function businessZone(business = {}) {
  return business.timezone || markets.find(m => m.code === business.countryCode)?.zones[0] || 'America/Panama';
}
function partsAt(value, zone = 'America/Panama') {
  if (!cache.has(zone)) cache.set(zone, new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, year:'numeric', month:'2-digit', day:'2-digit',
    hour:'2-digit', minute:'2-digit', second:'2-digit', hourCycle:'h23',
  }));
  const p = Object.fromEntries(cache.get(zone).formatToParts(new Date(+value)).map(p => [p.type,p.value]));
  return {date:`${p.year}-${p.month}-${p.day}`, time:`${p.hour}:${p.minute}`, second:p.second};
}
function offsetMinutes(value, zone) {
  const p = partsAt(value, zone);
  return (Date.parse(`${p.date}T${p.time}:${p.second}Z`) - Math.floor(+value/1000)*1000)/60000;
}
function shiftDate(date, days) {
  return new Date(Date.parse(`${date}T12:00:00Z`) + days*86400000).toISOString().slice(0,10);
}
function localInstant(date, time, zone = 'America/Panama') {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time || '')) {
    throw Object.assign(new Error('Revisa la fecha y hora de la reserva.'), {code:'invalid-argument'});
  }
  const wall = Date.parse(`${date}T${time}:00Z`);
  const offsets = new Set([-86400000,0,86400000].map(delta => offsetMinutes(wall+delta, zone)));
  const candidates = [...offsets].map(offset => wall-offset*60000).filter(value => {
    const p = partsAt(value, zone); return p.date===date && p.time===time;
  });
  // Never silently shift a nonexistent time or guess between repeated hours.
  if(candidates.length!==1) throw Object.assign(new Error('Ese horario cambia por la zona horaria. Selecciona otra hora.'), {code:'invalid-argument'});
  return new Date(candidates[0]);
}
function scheduleFields(start, business = {}) {
  const timezone = businessZone(business);
  const {date,time} = partsAt(start, timezone);
  return {date,time,timezone,timezoneOffsetMinutes:offsetMinutes(start,timezone)};
}
function queryWindow(start, end, zone = 'America/Panama', padding = 1) {
  const first=shiftDate(partsAt(start,zone).date,-padding);
  const last=shiftDate(partsAt(+end-1,zone).date,padding);
  const dates=[];
  for(let date=first; date<=last; date=shiftDate(date,1)) dates.push(date);
  return {dates,from:localInstant(first,'00:00',zone),to:localInstant(shiftDate(last,1),'00:00',zone)};
}
export {businessZone,partsAt,offsetMinutes,shiftDate,localInstant,scheduleFields,queryWindow};

// A wall-clock Date is only for calendar widgets; never persist it as an instant.
export function wallDate(value, business={}) {
  const p=partsAt(value,businessZone(business));
  return new Date(`${p.date}T${p.time}:00`);
}
export function wallToInstant(value,business={}) {
  const d=new Date(value);
  const date=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const time=`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  return new Date(+localInstant(date,time,businessZone(business)) + d.getSeconds()*1000 + d.getMilliseconds());
}

export function businessWeekday(value = new Date(), business = {}) {
  const {date} = partsAt(value,businessZone(business));
  return ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date(`${date}T12:00:00Z`).getUTCDay()];
}
